import { Conversions } from "helpers";

/**
 * it deeply merges two objects (only given properties are replaced)
 * @param {object} source the source object 
 * @param {object} replacement the object to be merged with the source
 * @return {object} the merged object
 */


export const deepInterpolation = (source: any, replacement: any): any => {
  // Create a copy of the original object to avoid modifying the original data
  const result = { ...source };

  // Recursively merge the replacement object into the original object
  const mergeObjects = (obj1: any, obj2: any) => {
    for (const key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        // Check whether the property is an object for doing deep merging (if it is array, it must replace it)
        if (obj2[key] instanceof Object && obj1.hasOwnProperty(key) && obj1[key] instanceof Object && !Array.isArray(obj1[key])) {
          mergeObjects(obj1[key], obj2[key]);
        } else {
          obj1[key] = obj2[key];
        }
      }
    }
  };

  mergeObjects(result, replacement);

  return result;
}


/**
 * method that returns the propert text color according to the provided background color based on the luminance
 * @param {string} backgroundColor the background to be used as reference
 * @param {string} black the color to be used in case its black
 * @param {string} white the color to be used in case its white 
 * @return {string} the contrast color
 */

export const getContrast = (backgroundColor: string, black: string = "black", white: string = "white"): string => {
  // Helper function to calculate the luminance of an RGB color
  const getLuminance = ([r, g, b]: [number, number, number]): number => {
    const [rl, gl, bl] = [r / 255, g / 255, b / 255];
    const [rSrgb, gSrgb, bSrgb] = [
      rl <= 0.03928 ? rl / 12.92 : ((rl + 0.055) / 1.055) ** 2.4,
      gl <= 0.03928 ? gl / 12.92 : ((gl + 0.055) / 1.055) ** 2.4,
      bl <= 0.03928 ? bl / 12.92 : ((bl + 0.055) / 1.055) ** 2.4,
    ];
    return 0.2126 * rSrgb + 0.7152 * gSrgb + 0.0722 * bSrgb;
  };

  // Convert the background color to RGB format
  let rgbColor: [number, number, number];
  if (backgroundColor.startsWith("#")) {
    rgbColor = Conversions.toRgbAsHex(backgroundColor);
  } else if (backgroundColor.startsWith("rgb(")) {
    rgbColor = Conversions.toRgbAsString(backgroundColor);
  } else {
    throw new Error("Invalid background color format. Please provide a color in hex or RGB format.");
  }

  // Calculate the luminance of the background color
  const luminance = getLuminance(rgbColor);

  // Determine the recommended text color based on luminance
  return luminance > 0.5 ? black : white;
}